import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  AppointmentAlert,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  AudioIcon,
  Check,
  CycleIcon,
  DashboardIcon,
  Divider,
  Header,
  VideoIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { Container, Radio, Stack, Typography } from '@mui/material';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useGetLessonQuery, useUpdatePreferenceMutation } from '../../services/lessons';
import { formatError, generateImageAssestUrl } from '@classity-edtech/ui-base';
import { useNavigate, useParams } from 'react-router-dom';
import { webPaths } from '../../routes/paths';
import { updateFlashMessage } from '../../store/actions/flashMessage';
import { useAppDispatch } from '../../store/hooks';
import { Roles } from '@classity-edtech/platform-utils';
import { useGetMyProfileQuery } from '../../services/users';

const TextEditor = React.lazy(() =>
  import('@classity-edtech/ui-base/dist/components/Displays').then(mod => ({
    default: mod.TextEditor,
  })),
);

export interface HomeProps extends MuiBoxProps {}

export const WaitingRoom = ({ className }: HomeProps) => {
  const defaultLanguage = 'en';
  const dispatch: any = useAppDispatch();
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const [lessonDescription, setLessonDescription] = useState('');
  const [description, setDescription] = useState('');
  const [showDescriptionForm, setShowDescriptionForm] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef(null);

  const [camStatus, setCamStatus] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isMicOn, setIsMicOn] = useState(false);
  const [screenShareStatus, setScreenShareStatus] = useState(false);
  const [micStatus, setMicStatus] = useState(false);
  const [speakerStatus, setSpeakerStatus] = useState(false);
  const [connectivityStatus, setConnectivityStatus] = useState(false);

  const {
    data: user,
    isLoading: loadingUser,
    isUninitialized: uninitialisedUser,
  } = useGetMyProfileQuery();

  const { data: lesson } = useGetLessonQuery(
    {
      lessonId,
    },
    { skip: !user.id },
  );

  const [updatePreference, updatePreferenceResult] = useUpdatePreferenceMutation();

  const [status, setStatus] = useState('Testing...');
  const [stream, setStream] = useState(null);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
      videoRef.current.srcObject = mediaStream;
      await videoRef.current.play();
      setStatus('Granted');
      setIsCameraOn(true);
      setCamStatus(true);
    } catch (err) {
      setCamStatus(false);
      if (err.name === 'NotAllowedError') {
        setStatus('Denied');
      } else {
        setStatus('Error');
        console.error('Camera error:', err);
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setStatus('idle');
      setIsCameraOn(false);
    }
  };

  const testMicrophone = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(
        'https://static04.classity.co/aud/sunflower-street-drumloop-85bpm-163900.mp3',
      );
    }

    audioRef.current.play().catch(err => {
      console.error('Audio playback failed', err);
    });
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const testConnectivity = () => {
    setConnectivityStatus(navigator.onLine);
  };

  const checkMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleMicCheck = async () => {
    const hasMic = await checkMicrophone();
    setMicStatus(hasMic);
  };

  const checkScreenShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });

      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleScreenCheck = async () => {
    const canShare = await checkScreenShare();
    setScreenShareStatus(canShare);
  };

  const toggleDescriptionForm = () => {
    setShowDescriptionForm(value => !value);
  };

  const toggleIsMicOn = () => {
    setIsMicOn(value => !value);
  };

  const handleResponseUpdate = async () => {
    if (!lessonId || !description) return;

    const updatePreferenceResultData = await updatePreference({
      lessonId,
      description,
    });

    if ('error' in updatePreferenceResultData) {
      const error = formatError(updatePreferenceResultData.error.message);
      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'An error occurred!',
        }),
      );
    }

    if ('data' in updatePreferenceResultData) {
      setShowDescriptionForm(false);
    }
  };

  useEffect(() => {
    if (!user && !loadingUser && !uninitialisedUser) {
      navigate(`${webPaths.root}`, { replace: true });
    }
  }, [user]);

  useEffect(() => {
    if (lesson) {
      setLessonDescription(
        DOMPurify.sanitize(lesson?.description?.find((desc: any) => desc.language === 'en')?.text),
      );
    }
  }, [lesson]);

  useEffect(() => {
    setConnectivityStatus(navigator.onLine);
  }, []);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const appointmentAlert = {
    profile: {
      name: `${lesson?.organizer?.user?.profile?.firstname} ${lesson?.organizer?.user?.profile?.lastname}`,
      src: generateImageAssestUrl(lesson?.organizer?.user?.profile?.avatar?.contentUrl, 2),
      bio: lesson?.organizer?.user?.profile?.bio,
    },
    cta: {
      element: (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography variant='body2SemiBold' sx={{ textTransform: 'uppercase' }}>
            Join
          </Typography>
          <ArrowDownIcon sx={{ width: '14px', height: '14px', transform: 'rotate(-90deg)' }} />
        </Box>
      ),
      action: () => {},
    },
    appointmentDate: lesson?.event?.startTime?.dateTime
      ? new Date(lesson?.event?.startTime?.dateTime)
      : new Date(),
    info: 'Join the session in progress...',
  };

  return (
    <>
      <Container className={className} sx={{}}>
        <Header />
        <Box
          sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, alignItems: 'center', mt: 5, mb: 1 }}
        >
          <ArrowRightIcon
            sx={{ width: '32px', height: 'auto', transform: 'rotate(180deg)', mt: -1.5 }}
          />
          <Typography variant='subtitle2'>
            {lesson?.name?.find(n => n.language === defaultLanguage)?.displayName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column-reverse', md: 'row' } }}>
          <Stack sx={{ maxWidth: '360px', gap: 6 }}>
            <AppointmentAlert
              sx={{ minWidth: { xs: '100%', md: '360px' } }}
              {...appointmentAlert}
            />

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography variant='subtitle4'>What to expect</Typography>

                {user?.roleId === Roles.TUTOR && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                    onClick={toggleDescriptionForm}
                  >
                    <Typography variant='body3'>Edit</Typography>
                    <DashboardIcon sx={{ width: '14px', height: '14px' }} />
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 1 }} />

              <Stack sx={{ gap: 1.5 }}>
                {!showDescriptionForm ? (
                  <Typography variant='body3'>
                    <div dangerouslySetInnerHTML={{ __html: lessonDescription }} />
                  </Typography>
                ) : (
                  <>
                    {typeof window !== 'undefined' && (
                      <Suspense fallback={<div>Loading editor...</div>}>
                        <TextEditor
                          content={description}
                          setContent={setDescription}
                          sendMessage={handleResponseUpdate}
                          sendingMessages={updatePreferenceResult.isLoading}
                          isCompact
                        />
                      </Suspense>
                    )}
                  </>
                )}
              </Stack>
            </Box>

            {false && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                }}
              >
                <ArrowLeftIcon
                  sx={{ width: '14px', height: '14px', transform: 'rotate(180deg)' }}
                />
                <Typography variant='body3'>Games</Typography>
              </Box>
            )}
          </Stack>
          <Stack sx={{ gap: 3 }}>
            <Stack
              sx={{
                height: { xs: '189', md: '362px' },
                aspectRatio: '1.5/1',
                position: 'relative',
                py: 1.5,
                px: 1,
                borderRadius: '8px',
                justifyContent: 'space-between',
              }}
            >
              <video
                ref={videoRef}
                width='320'
                height='240'
                autoPlay
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '8px',
                  filter: 'brightness(70%)',
                  backgroundColor: 'black',
                  objectFit: 'cover',
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'space-between',
                  position: 'relative',
                  zIndex: 2,
                  color: globalColours['$global-colour-base-contrast'],
                  backgroundColor: globalColours['$global-colour-background-overlay'],
                  mx: -1,
                  my: -1.5,
                  px: 1,
                  py: 1.5,
                }}
              >
                <Typography variant='body3'>Checking your device readiness...</Typography>
                <Typography variant='body3'>{status}</Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  position: 'relative',
                  zIndex: 2,
                  color: globalColours['$global-colour-base-contrast'],
                  mx: 'auto',
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    minWidth: '75px',
                    display: 'flex',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: isMicOn
                      ? globalColours['$global-colour-interactive-accent-active']
                      : '',
                  }}
                  onClick={toggleIsMicOn}
                >
                  <AudioIcon />
                </Box>
                <Box
                  sx={{
                    p: 1,
                    minWidth: '75px',
                    display: 'flex',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: isCameraOn
                      ? globalColours['$global-colour-interactive-accent-active']
                      : '',
                  }}
                  onClick={isCameraOn ? stopCamera : startCamera}
                >
                  <VideoIcon />
                </Box>
              </Box>
            </Stack>

            <Stack sx={{ gap: 4 }}>
              <Box sx={{ display: 'flex' }}>
                <Stack sx={{ minWidth: '170px' }}>
                  <Typography variant='body3SemiBold'>Speaker</Typography>
                  <Typography variant='body3'>Can you hear a sound?</Typography>
                </Stack>

                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      gap: 1.5,
                      cursor: 'pointer',
                    }}
                  >
                    <Radio
                      disableRipple
                      checked={speakerStatus}
                      onChange={() => {
                        setSpeakerStatus(true);
                        pauseAudio();
                      }}
                      sx={{ p: 0 }}
                    />
                    <Typography variant='body3'>Yes</Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      gap: 1.5,
                      cursor: 'pointer',
                    }}
                  >
                    <Radio
                      disableRipple
                      checked={!speakerStatus}
                      onChange={() => {
                        setSpeakerStatus(false);
                        pauseAudio();
                      }}
                      sx={{ p: 0 }}
                    />
                    <Typography variant='body3'>No</Typography>
                  </Box>

                  <CycleIcon sx={{ width: '16px', height: '16px' }} onClick={testMicrophone} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <Stack sx={{ minWidth: '170px' }}>
                  <Typography variant='body3SemiBold'>Microphone</Typography>
                </Stack>

                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, gap: 1.5 }}>
                    {micStatus ? (
                      <>
                        <Typography variant='body3'>Ready</Typography>
                        <Check sx={{ width: '16px', height: '16px' }} />
                      </>
                    ) : (
                      <Typography variant='body3'>Testing</Typography>
                    )}
                    {!micStatus && (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: globalColours['$global-colour-core-brand-1'],
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                            }}
                          ></Box>
                          <Box
                            sx={{
                              backgroundColor: globalColours['$global-colour-core-brand-1'],
                              opacity: 0.08,
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                            }}
                          ></Box>
                          <Box
                            sx={{
                              backgroundColor: globalColours['$global-colour-core-brand-1'],
                              opacity: 0.08,
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                            }}
                          ></Box>
                        </Box>
                      </>
                    )}

                    <CycleIcon sx={{ width: '16px', height: '16px' }} onClick={handleMicCheck} />
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <Stack sx={{ minWidth: '170px' }}>
                  <Typography variant='body3SemiBold'>Camera</Typography>
                </Stack>

                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, gap: 1.5 }}>
                    {camStatus ? (
                      <>
                        <Typography variant='body3'>Ready</Typography>
                        <Check sx={{ width: '16px', height: '16px' }} />
                      </>
                    ) : (
                      <Typography variant='body3'>Pending</Typography>
                    )}

                    {!camStatus && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: globalColours['$global-colour-content-primary'],
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                          }}
                        ></Box>
                        <Box
                          sx={{
                            backgroundColor: globalColours['$global-colour-content-primary'],
                            opacity: 0.08,
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                          }}
                        ></Box>
                        <Box
                          sx={{
                            backgroundColor: globalColours['$global-colour-content-primary'],
                            opacity: 0.08,
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                          }}
                        ></Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <Stack sx={{ minWidth: '170px' }}>
                  <Typography variant='body3SemiBold'>Screen Share</Typography>
                </Stack>

                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, gap: 0.5 }}>
                    {screenShareStatus ? (
                      <>
                        <Typography variant='body3'>Ready</Typography>
                        <Check sx={{ width: '16px', height: '16px' }} />
                      </>
                    ) : (
                      <Typography variant='body3'>Testing</Typography>
                    )}
                  </Box>
                  <CycleIcon sx={{ width: '16px', height: '16px' }} onClick={handleScreenCheck} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <Stack sx={{ minWidth: '170px' }}>
                  <Typography variant='body3SemiBold'>Connectivity</Typography>
                </Stack>

                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, gap: 0.5 }}>
                    {connectivityStatus ? (
                      <>
                        <Typography variant='body3'>Ready</Typography>
                        <Check sx={{ width: '16px', height: '16px' }} />
                      </>
                    ) : (
                      <>
                        <Typography variant='body3'>Failed</Typography>
                        <ErrorOutlineIcon sx={{ width: '16px', height: '16px' }} />
                      </>
                    )}
                  </Box>
                  {!connectivityStatus && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', px: 1.5, gap: 0.5 }}
                      onClick={testConnectivity}
                    >
                      <Typography variant='body3'>Retry</Typography>
                      <CycleIcon sx={{ width: '16px', height: '16px' }} />
                    </Box>
                  )}
                </Box>
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </>
  );
};

WaitingRoom.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

WaitingRoom.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(WaitingRoom)(
  ({ theme }) => `
`,
);
